import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK = "#1A1A2E";
const BG = "#F8F9FA";
const MUTED = "#5b5e69";

/**
 * Contractor dashboard — Reactivation SMS Campaign (Front Office only).
 * Expected URL: /contractor/{contractor_id}/reactivate
 * We derive contractor_id from the path or query param and gate on plan.
 *
 * MVP: upload CSV of past customers, personalize a template, send throttled
 * (1 msg/sec) via Telnyx, track sends / replies / opt-outs.
 */
export default function ReactivationPage() {
  const contractorId =
    new URLSearchParams(window.location.search).get("cid") ||
    localStorage.getItem("nn_contractor_id") ||
    "";

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateError, setGateError] = useState(null);

  const [listName, setListName] = useState("");
  const [template, setTemplate] = useState("");
  const [csvText, setCsvText] = useState("");
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  const load = async () => {
    if (!contractorId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get("/reactivation/campaigns", { params: { contractor_id: contractorId } })
                          .then((x) => x.data);
      setCampaigns(r.campaigns || []);
      setGateError(null);
    } catch (e) {
      if (e?.response?.status === 402) setGateError(e.response.data.detail);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const readCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result);
    reader.readAsText(file);
  };

  const submitCampaign = async (e) => {
    e.preventDefault();
    if (!csvText.trim() || !listName.trim()) return;
    setCreating(true); setCreateMsg(null);
    try {
      const r = await api.post("/reactivation/campaigns", {
        contractor_id: contractorId,
        list_name: listName,
        csv_text: csvText,
        template: template || undefined,
      }).then((x) => x.data);
      if (r.ok) {
        setCreateMsg({ ok: true, text: `Created — ${r.total_recipients} recipients ready. ${r.warnings?.length ? r.warnings.length + " row(s) skipped." : ""}` });
        setListName(""); setCsvText(""); setTemplate("");
        await load();
      } else {
        setCreateMsg({ ok: false, text: r.error || "Failed to create campaign." });
      }
    } catch (err) {
      setCreateMsg({ ok: false, text: err?.response?.data?.detail || err?.message || "Failed to create campaign." });
    } finally {
      setCreating(false);
    }
  };

  const control = async (id, action) => {
    try {
      await api.post(`/reactivation/campaigns/${id}/control`,
                       { contractor_id: contractorId, action })
                .then((x) => x.data);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e?.message); }
  };

  const start = async (id) => {
    if (!window.confirm("Start sending SMS to every pending recipient? This runs at 1 msg/second.")) return;
    try {
      await api.post(`/reactivation/campaigns/${id}/start?contractor_id=${contractorId}`)
                .then((x) => x.data);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || e?.message); }
  };

  if (!contractorId) {
    return (
      <div style={pad}><h2 style={{ color: NAVY }}>Sign in as a contractor to use Reactivation Campaigns.</h2></div>
    );
  }

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "30px 20px" }} data-testid="reactivation-page">
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
          FRONT OFFICE · REACTIVATION CAMPAIGNS
        </div>
        <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 3, color: NAVY, fontSize: 44, margin: "4px 0 8px" }}>
          WAKE UP YOUR PAST CUSTOMERS.
        </h1>
        <p style={{ color: MUTED, marginBottom: 22, maxWidth: 660 }}>
          Upload a CSV of past customers (columns: <code>name, phone</code>). We&apos;ll text every one on your behalf
          at 1 message per second so we never trip Telnyx&apos;s spam filter, honor STOP replies automatically,
          and route every real reply into your inbox.
        </p>

        {gateError && (
          <div data-testid="reactivation-gate" style={warnBox}>
            {gateError} <a href="/pricing" style={{ color: NAVY, fontWeight: 700 }}>See Front Office →</a>
          </div>
        )}

        {!gateError && (
          <>
            <form onSubmit={submitCampaign} style={card} data-testid="reactivation-create-form">
              <h2 style={h2}>NEW CAMPAIGN</h2>

              <label style={label}>List name</label>
              <input value={listName} onChange={(e) => setListName(e.target.value)} required
                     data-testid="reactivation-list-name-input"
                     placeholder="Spring 2026 — furnace tune-ups"
                     style={input} />

              <label style={label}>Message template
                <span style={{ color: MUTED, fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                  {"{first_name}"} and {"{business_name}"} auto-fill. STOP compliance appended.
                </span>
              </label>
              <textarea value={template} onChange={(e) => setTemplate(e.target.value)}
                        data-testid="reactivation-template-input"
                        placeholder='Leave blank for our default template.'
                        rows={4} maxLength={600}
                        style={{ ...input, resize: "vertical" }} />

              <label style={label}>Customer list (CSV)
                <span style={{ color: MUTED, fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                  Columns like <code>name, phone</code> (or first_name/last_name/phone). Paste OR upload.
                </span>
              </label>
              <input type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && readCsvFile(e.target.files[0])}
                     data-testid="reactivation-csv-file-input"
                     style={{ marginBottom: 8 }} />
              <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)}
                        data-testid="reactivation-csv-text-input"
                        placeholder={`name,phone\nJohn Smith,4015551234\nJane Doe,4014441111`}
                        rows={6}
                        style={{ ...input, fontFamily: "Menlo, monospace", fontSize: 13, resize: "vertical" }} required />

              <button type="submit" disabled={creating}
                      data-testid="reactivation-create-submit"
                      style={btnGold}>
                {creating ? "CREATING…" : "CREATE CAMPAIGN →"}
              </button>
              {createMsg && (
                <div data-testid="reactivation-create-msg" style={createMsg.ok ? okBox : warnBox}>
                  {createMsg.text}
                </div>
              )}
            </form>

            <div style={{ ...card, marginTop: 20 }} data-testid="reactivation-campaigns-list">
              <h2 style={h2}>YOUR CAMPAIGNS</h2>
              {loading && <p>Loading…</p>}
              {!loading && campaigns.length === 0 && (
                <p style={{ color: MUTED }}>No campaigns yet. Create one above.</p>
              )}
              {campaigns.map((c) => (
                <div key={c.id} data-testid={`campaign-row-${c.id}`}
                     style={{ padding: "14px 0", borderTop: "1px solid #eef0f4", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 22, color: NAVY, letterSpacing: 1 }}>
                      {c.list_name}
                    </div>
                    <div style={{ color: MUTED, fontSize: 13 }}>
                      {c.total} recipients · sent {c.sent} · replied {c.replies} · opted out {c.opt_outs} · failed {c.failed}
                    </div>
                    <div style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>
                      Created: {c.created_at?.slice(0, 16).replace("T", " ")} · Status: <strong>{c.status.toUpperCase()}</strong>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {(c.status === "queued" || c.status === "paused") && (
                      <button onClick={() => start(c.id)} data-testid={`campaign-start-${c.id}`} style={btnPrimarySm}>
                        {c.status === "paused" ? "RESUME" : "START →"}
                      </button>
                    )}
                    {c.status === "sending" && (
                      <button onClick={() => control(c.id, "pause")} data-testid={`campaign-pause-${c.id}`} style={btnGhostSm}>
                        PAUSE
                      </button>
                    )}
                    {c.status !== "completed" && c.status !== "cancelled" && (
                      <button onClick={() => control(c.id, "cancel")} data-testid={`campaign-cancel-${c.id}`} style={btnDangerSm}>
                        CANCEL
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const pad = { padding: 40, textAlign: "center" };
const h2 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 24, margin: "0 0 14px" };
const card = { background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, padding: 22 };
const label = { display: "block", color: NAVY, fontSize: 11, letterSpacing: 2, fontWeight: 700, marginBottom: 4, marginTop: 12 };
const input = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 4 };
const btnGold = { background: GOLD, color: NAVY, border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 800, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 14 };
const btnPrimarySm = { background: GOLD, color: NAVY, border: "none", padding: "8px 14px", borderRadius: 8, fontWeight: 700, letterSpacing: 1, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const btnGhostSm = { background: "transparent", color: NAVY, border: `1.5px solid ${NAVY}`, padding: "8px 14px", borderRadius: 8, fontWeight: 700, letterSpacing: 1, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const btnDangerSm = { background: "transparent", color: "#A32D2D", border: `1.5px solid #A32D2D`, padding: "8px 14px", borderRadius: 8, fontWeight: 700, letterSpacing: 1, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const okBox = { marginTop: 12, background: "#E7F5EE", color: "#0F5132", padding: "10px 14px", borderRadius: 10, fontSize: 14 };
const warnBox = { marginTop: 12, background: "#FDECEC", color: "#A32D2D", padding: "12px 16px", borderRadius: 10, fontSize: 14 };
